import { useState } from 'react';
import { IconBrandGoogleDrive, IconCheck, IconX } from '@tabler/icons-react';
import Btn from './ui/Btn';
import { api, ApiError } from '../lib/api';
import { toast } from '../stores/toaster';

type PieceKind = 'piece_client' | 'piece_adverse' | 'other_document' | 'pdf_file';
type PushSubfolder = 'PROCEDURE' | 'PIECESCL' | 'PIECESADV' | 'COURRIERS' | 'EXPERTISE';

type PushResponse = {
  drive_file_id: string;
  drive_file_name: string;
  drive_folder_id: string;
  ref_forlex: string;
  subfolder: string;
};

type Props = {
  dossierId: string;
  kind: PieceKind;
  pieceId: string;
  pieceTitle?: string;
  /** Sous-folder pré-sélectionné. Par défaut selon `kind`. */
  defaultSubfolder?: PushSubfolder;
  /** Variante d'affichage : 'icon' ou 'full' (avec label). */
  variant?: 'icon' | 'full';
};

const SUBFOLDER_OPTIONS: { value: PushSubfolder; label: string }[] = [
  { value: 'PROCEDURE', label: 'PROCEDURE (conclusions, actes, jugements)' },
  { value: 'PIECESCL', label: 'PIECESCL (pièces client)' },
  { value: 'PIECESADV', label: 'PIECESADV (pièces adverses)' },
  { value: 'COURRIERS', label: 'COURRIERS' },
  { value: 'EXPERTISE', label: 'EXPERTISE' },
];

const DEFAULT_SUBFOLDER_BY_KIND: Record<PieceKind, PushSubfolder> = {
  piece_client: 'PIECESCL',
  piece_adverse: 'PIECESADV',
  other_document: 'COURRIERS',
  pdf_file: 'PROCEDURE',
};

export default function PushToDriveButton({
  dossierId,
  kind,
  pieceId,
  pieceTitle,
  defaultSubfolder,
  variant = 'full',
}: Props) {
  const [open, setOpen] = useState(false);
  const [subfolder, setSubfolder] = useState<PushSubfolder>(
    defaultSubfolder || DEFAULT_SUBFOLDER_BY_KIND[kind],
  );
  const [filename, setFilename] = useState('');
  const [pushing, setPushing] = useState(false);

  const submit = async () => {
    setPushing(true);
    try {
      const result = await api.post<PushResponse>(`/dossiers/${dossierId}/gdrive/push-piece`, {
        kind,
        piece_id: pieceId,
        subfolder,
        filename: filename || undefined,
      });
      toast.success(
        `Envoyé vers Drive : ${result.ref_forlex}/${result.subfolder}/${result.drive_file_name}`,
      );
      setOpen(false);
    } catch (e) {
      const err = e as ApiError;
      const msg = err?.message || err?.problem?.detail || err?.problem?.title || 'inconnu';
      if (err?.status === 503) {
        toast.error('Drive non configuré côté backend.');
      } else if (err?.status === 404) {
        toast.error('Dossier introuvable dans Drive : ' + msg);
      } else {
        toast.error('Échec push : ' + msg);
      }
    } finally {
      setPushing(false);
    }
  };

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded p-1 text-text-tertiary hover:bg-paper-card hover:text-text-primary"
          title="Envoyer vers Google Drive"
          aria-label="Envoyer vers Drive"
        >
          <IconBrandGoogleDrive size={14} stroke={1.6} />
        </button>
      ) : (
        <Btn variant="ghost" size="sm" onClick={() => setOpen(true)}>
          <IconBrandGoogleDrive size={14} stroke={1.6} className="mr-1" />
          Vers Drive
        </Btn>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !pushing && setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-md bg-paper-elevated shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between border-b border-rule px-5 py-3">
              <div>
                <h2 className="font-serif text-[16px] font-medium text-text-primary">
                  Enregistrer vers Google Drive
                </h2>
                <p className="mt-0.5 text-[11px] text-text-secondary">
                  {pieceTitle ? <em>{pieceTitle}</em> : 'Pièce sélectionnée'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => !pushing && setOpen(false)}
                className="rounded p-1 text-text-tertiary hover:bg-paper-card hover:text-text-primary"
                aria-label="Fermer"
                disabled={pushing}
              >
                <IconX size={16} stroke={1.6} />
              </button>
            </header>

            <div className="space-y-3 px-5 py-4">
              <label className="block text-[12px]">
                <span className="text-text-secondary">Sous-folder Drive cible</span>
                <select
                  value={subfolder}
                  onChange={(e) => setSubfolder(e.target.value as PushSubfolder)}
                  className="mt-1 w-full rounded border border-rule bg-paper-card px-2 py-1.5 text-[13px]"
                  disabled={pushing}
                >
                  {SUBFOLDER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block text-[12px]">
                <span className="text-text-secondary">Nom de fichier sur Drive (optionnel)</span>
                <input
                  type="text"
                  value={filename}
                  onChange={(e) => setFilename(e.target.value)}
                  placeholder="laisse vide pour garder le nom d'origine"
                  className="mt-1 w-full rounded border border-rule bg-paper-card px-2 py-1.5 text-[13px]"
                  disabled={pushing}
                />
              </label>

              <p className="rounded border border-rule-strong/40 bg-paper-card px-3 py-2 text-[11px] text-text-tertiary">
                <IconCheck size={12} stroke={1.6} className="-mt-0.5 mr-1 inline" />
                La sous-farde est créée si manquante. Le folder client ne sera{' '}
                <strong>jamais</strong> créé automatiquement (règle cabinet).
              </p>
            </div>

            <footer className="flex justify-end gap-2 border-t border-rule px-5 py-3">
              <Btn variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={pushing}>
                Annuler
              </Btn>
              <Btn
                variant="primary"
                size="sm"
                onClick={() => void submit()}
                disabled={pushing}
              >
                {pushing ? 'Envoi…' : 'Envoyer vers Drive'}
              </Btn>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
